import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Buildings,
  MagnifyingGlass,
  FileCsv,
  ArrowLeft,
  ArrowRight,
  Crown,
  Prohibit,
  CheckCircle,
  Eye,
} from "@phosphor-icons/react";
import { api, formatBRL, formatNum, formatDate, BACKEND_URL } from "@/lib/gado";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const PAGE_SIZE = 12;

function QuotaBar({ label, used, limit, testid }) {
  const unlimited = limit == null;
  const pct = unlimited ? (used > 0 ? 35 : 4) : Math.min(100, Math.round((used / Math.max(limit, 1)) * 100));
  const capped = !unlimited && used >= limit;
  return (
    <div className="min-w-0" data-testid={testid}>
      <div className="flex justify-between gap-2 text-[10px] font-semibold uppercase tracking-wider text-gado-muted">
        <span className="truncate">{label}</span>
        <span className={capped ? "text-destructive" : ""}>
          {formatNum(used, 0)}{unlimited ? "" : `/${limit}`}
        </span>
      </div>
      <div className="mt-1 h-1.5 rounded-full bg-gado-line/60 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${capped ? "bg-destructive" : "bg-gado-green"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function OrgCard({ org, onPlan, onToggleBlock, onDetail }) {
  const suspended = org.blocked;
  return (
    <div
      className="rounded-xl border border-gado-line bg-white p-4 space-y-3"
      data-testid={`org-card-${org.user_id}`}
    >
      <div className="flex items-start justify-between gap-2 min-w-0">
        <div className="min-w-0">
          <p className="font-cabinet font-bold text-gado-ink truncate">{org.organization || "Sem nome"}</p>
          <p className="text-xs text-gado-muted truncate">{org.email}</p>
        </div>
        <div className="flex gap-1.5 shrink-0">
          <Badge
            variant="outline"
            className={org.plan === "pro"
              ? "border-gado-gold/40 bg-gado-gold/10 text-gado-gold"
              : "border-gado-line bg-gado-bg text-gado-muted"}
            data-testid={`org-plan-${org.user_id}`}
          >
            {org.plan === "pro" ? "Pro" : "Grátis"}
          </Badge>
          <Badge
            variant="outline"
            className={suspended
              ? "border-destructive/40 bg-destructive/10 text-destructive"
              : "border-gado-green/30 bg-gado-green/10 text-gado-green"}
            data-testid={`org-status-${org.user_id}`}
          >
            {suspended ? "Suspensa" : "Ativa"}
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-center rounded-lg bg-gado-bg/60 py-2">
        <div>
          <p className="text-[9px] uppercase tracking-wider text-gado-muted font-semibold">Pesagens/mês</p>
          <p className="font-mono text-sm font-bold text-gado-ink tabular-nums">{formatNum(org.month.pesagens, 0)}</p>
        </div>
        <div>
          <p className="text-[9px] uppercase tracking-wider text-gado-muted font-semibold">Animais/mês</p>
          <p className="font-mono text-sm font-bold text-gado-ink tabular-nums">{formatNum(org.month.animais, 0)}</p>
        </div>
        <div>
          <p className="text-[9px] uppercase tracking-wider text-gado-muted font-semibold">Valor/mês</p>
          <p className="font-mono text-sm font-bold text-gado-ink tabular-nums truncate">{formatBRL(org.month.valor)}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <QuotaBar label="PDFs no mês" used={org.month.pdfs_used} limit={org.month.pdfs_limit} testid={`org-pdfs-${org.user_id}`} />
        <QuotaBar label="IA no mês" used={org.month.ia_used} limit={org.month.ia_limit} testid={`org-ia-${org.user_id}`} />
      </div>

      <p className="text-[11px] text-gado-muted">
        Último acesso: {org.last_login_at ? formatDate(org.last_login_at) : "nunca"}
        {" · "}desde {org.created_at ? formatDate(org.created_at) : "—"}
      </p>

      <div className="flex flex-wrap gap-2 pt-1">
        <Button size="sm" variant="outline" onClick={() => onDetail(org)} data-testid={`org-detail-${org.user_id}`}>
          <Eye size={14} className="mr-1" /> Detalhes
        </Button>
        <Button size="sm" variant="outline" onClick={() => onPlan(org)} data-testid={`org-plan-btn-${org.user_id}`}>
          <Crown size={14} className="mr-1" /> Plano
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onToggleBlock(org)}
          data-testid={`org-block-btn-${org.user_id}`}
          className={suspended ? "text-gado-green" : "text-destructive"}
        >
          {suspended ? <><CheckCircle size={14} className="mr-1" /> Reativar</> : <><Prohibit size={14} className="mr-1" /> Suspender</>}
        </Button>
      </div>
    </div>
  );
}

export default function AdminOrganizations() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [qApplied, setQApplied] = useState("");
  const [plan, setPlan] = useState("todos");
  const [status, setStatus] = useState("todos");
  const [page, setPage] = useState(1);
  const [planOrg, setPlanOrg] = useState(null);
  const [planValue, setPlanValue] = useState("free");
  const [blockOrg, setBlockOrg] = useState(null);
  const [detailOrg, setDetailOrg] = useState(null);
  const [saving, setSaving] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-orgs", qApplied, plan, status, page],
    queryFn: async () =>
      (await api.get("/admin/organizations", {
        params: {
          q: qApplied || undefined,
          plan: plan === "todos" ? undefined : plan,
          status: status === "todos" ? undefined : status,
          page,
          page_size: PAGE_SIZE,
        },
      })).data,
  });

  const { data: detail } = useQuery({
    queryKey: ["admin-org-detail", detailOrg?.user_id],
    queryFn: async () => (await api.get(`/admin/users/${detailOrg.user_id}`)).data,
    enabled: !!detailOrg,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-orgs"] });

  const savePlan = async () => {
    if (!planOrg) return;
    setSaving(true);
    try {
      await api.put(`/admin/users/${planOrg.user_id}/plan`, { plan: planValue });
      toast.success(`Plano de ${planOrg.organization || planOrg.email} alterado para ${planValue === "pro" ? "Pro" : "Grátis"}`);
      setPlanOrg(null);
      refresh();
    } catch {
      toast.error("Falha ao alterar plano");
    } finally {
      setSaving(false);
    }
  };

  const confirmBlock = async () => {
    if (!blockOrg) return;
    setSaving(true);
    try {
      await api.post(`/admin/users/${blockOrg.user_id}/block`, { blocked: !blockOrg.blocked });
      toast.success(blockOrg.blocked ? "Organização reativada" : "Organização suspensa — sessões encerradas");
      setBlockOrg(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao atualizar status");
    } finally {
      setSaving(false);
    }
  };

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem("gadopro_token");
      const resp = await fetch(`${BACKEND_URL}/api/admin/organizations/export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "organizacoes_gadopro.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV exportado");
    } catch {
      toast.error("Falha ao exportar");
    }
  };

  const items = data?.items || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-5" data-testid="admin-organizations-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-cabinet text-2xl md:text-3xl font-bold text-gado-ink tracking-tight">Organizações</h1>
          <p className="text-gado-muted text-sm">Cada conta é uma organização — status, consumo e plano.</p>
        </div>
        <Button onClick={exportCsv} variant="outline" data-testid="orgs-export-csv" className="shrink-0">
          <FileCsv size={16} className="mr-1.5" /> Exportar CSV
        </Button>
      </div>

      {/* Filtros */}
      <div className="flex flex-col md:flex-row gap-2">
        <form
          className="flex gap-2 flex-1"
          onSubmit={(e) => { e.preventDefault(); setPage(1); setQApplied(q); }}
        >
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nome, organização ou e-mail…"
            data-testid="orgs-search-input"
            className="bg-white"
          />
          <Button type="submit" variant="outline" data-testid="orgs-search-button" aria-label="Buscar">
            <MagnifyingGlass size={16} />
          </Button>
        </form>
        <Select value={plan} onValueChange={(v) => { setPlan(v); setPage(1); }}>
          <SelectTrigger className="w-full md:w-36 bg-white" data-testid="orgs-filter-plan">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os planos</SelectItem>
            <SelectItem value="free">Grátis</SelectItem>
            <SelectItem value="pro">Pro</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger className="w-full md:w-36 bg-white" data-testid="orgs-filter-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os status</SelectItem>
            <SelectItem value="ativo">Ativas</SelectItem>
            <SelectItem value="suspenso">Suspensas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[...Array(4)].map((_, i) => <div key={i} className="h-48 rounded-xl bg-gado-line/40 animate-pulse" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-gado-line bg-white p-10 text-center" data-testid="orgs-empty">
          <Buildings size={32} className="mx-auto text-gado-muted mb-2" />
          <p className="text-sm text-gado-muted">Nenhuma organização encontrada.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((o) => (
            <OrgCard
              key={o.user_id}
              org={o}
              onDetail={setDetailOrg}
              onPlan={(org) => { setPlanOrg(org); setPlanValue(org.plan); }}
              onToggleBlock={setBlockOrg}
            />
          ))}
        </div>
      )}

      {/* Paginação */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-gado-muted" data-testid="orgs-total">{total} organização(ões)</p>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="orgs-prev-page" aria-label="Página anterior">
            <ArrowLeft size={14} />
          </Button>
          <span className="text-xs text-gado-muted tabular-nums">{page}/{totalPages}</span>
          <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="orgs-next-page" aria-label="Próxima página">
            <ArrowRight size={14} />
          </Button>
        </div>
      </div>

      {/* Dialog: trocar plano */}
      <Dialog open={!!planOrg} onOpenChange={(open) => !open && setPlanOrg(null)}>
        <DialogContent data-testid="org-plan-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-cabinet">Alterar plano</DialogTitle>
            <DialogDescription>{planOrg?.organization || planOrg?.email}</DialogDescription>
          </DialogHeader>
          <Select value={planValue} onValueChange={setPlanValue}>
            <SelectTrigger data-testid="org-plan-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="free">Grátis</SelectItem>
              <SelectItem value="pro">Pro</SelectItem>
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button onClick={savePlan} disabled={saving} data-testid="org-plan-save" className="w-full bg-gado-green hover:bg-gado-greenDark text-white">
              {saving ? "Salvando…" : "Salvar plano"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: suspender/reativar */}
      <Dialog open={!!blockOrg} onOpenChange={(open) => !open && setBlockOrg(null)}>
        <DialogContent data-testid="org-block-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-cabinet">
              {blockOrg?.blocked ? "Reativar organização" : "Suspender organização"}
            </DialogTitle>
            <DialogDescription>
              {blockOrg?.blocked
                ? `${blockOrg?.organization || blockOrg?.email} voltará a acessar o app normalmente.`
                : `${blockOrg?.organization || blockOrg?.email} perderá o acesso imediatamente e as sessões serão encerradas.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={confirmBlock}
              disabled={saving}
              data-testid="org-block-confirm"
              className={blockOrg?.blocked
                ? "w-full bg-gado-green hover:bg-gado-greenDark text-white"
                : "w-full bg-destructive hover:bg-destructive/90 text-white"}
            >
              {saving ? "Aplicando…" : blockOrg?.blocked ? "Reativar" : "Suspender"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: detalhes */}
      <Dialog open={!!detailOrg} onOpenChange={(open) => !open && setDetailOrg(null)}>
        <DialogContent data-testid="org-detail-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-cabinet">{detailOrg?.organization || "Organização"}</DialogTitle>
            <DialogDescription>{detailOrg?.email}</DialogDescription>
          </DialogHeader>
          {!detail ? (
            <div className="h-24 rounded-xl bg-gado-line/40 animate-pulse" />
          ) : (
            <div className="grid grid-cols-2 gap-2 text-sm" data-testid="org-detail-usage">
              {[
                ["Pesagens (total)", formatNum(detail.usage.pesagens, 0)],
                ["Animais (total)", formatNum(detail.usage.animais, 0)],
                ["Arrobas (total)", `${formatNum(detail.usage.arrobas, 1)}@`],
                ["Valor comprado", formatBRL(detail.usage.valor_total)],
                ["Análises de IA", formatNum(detail.usage.analises_ia, 0)],
                ["Última pesagem", detail.usage.ultima_pesagem ? formatDate(detail.usage.ultima_pesagem) : "nunca"],
              ].map(([label, value]) => (
                <div key={label} className="rounded-lg bg-gado-bg/60 p-2.5">
                  <p className="text-[9px] uppercase tracking-wider text-gado-muted font-semibold">{label}</p>
                  <p className="font-mono text-sm font-bold text-gado-ink tabular-nums truncate">{value}</p>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
